// react
import { useEffect } from 'react';

// tanstack
import { useMatch } from '@tanstack/react-router';

// components
import { useBreadcrumbContext } from '@/app/BreadcrumbContext';

/**
 * Overrides the breadcrumb label for the current route match with a
 * dynamically computed value (e.g. a loaded entity's name), taking
 * precedence over the route's static `staticData.crumb`
 * (see `AppBreadcrumbs.tsx` and `src/types/tanstack-router.d.ts`).
 *
 * Pass `null`/`undefined` (e.g. while the entity is still loading) to fall
 * back to the route's static crumb - or to hide the crumb entirely if the
 * route has none - instead of showing a stale/wrong label.
 *
 * @example
 * // $energyGroupId/route.tsx has no static `crumb` (so the raw id is
 * // never shown), the loaded page fills in the real name instead:
 * const { data: energyGroup } = useGetEnergyGroupByIdQuery(energyGroupId);
 * useBreadcrumb(energyGroup?.Name);
 */
export function useBreadcrumb(label: string | null | undefined) {
  const { setOverride, clearOverride } = useBreadcrumbContext();
  const { id: matchId } = useMatch({ strict: false });

  useEffect(() => {
    if (!label) {
      clearOverride(matchId);
      return;
    }

    setOverride(matchId, label);

    return () => clearOverride(matchId);
  }, [matchId, label, setOverride, clearOverride]);
}
